import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState, useMemo } from 'react';
import { useNotify } from 'react-admin';
import {
  AgentAvailabilityStatus,
  AgentTeamRole,
  AgentTrackingNumberUsageCategory,
  CallQueue,
  Team,
  TeamAssignment,
  TrackingNumberListItem,
} from '../../../interfaces/calls';
import agentsService from '../../../services/appointments/agents';
import voiceService from '../../../services/appointments/calls';
import queuesService from '../../../services/appointments/queues';
import teamsService from '../../../services/appointments/teams';
import coreServiceReal from '../../../services/core';
import useDebounce from '../../../hooks/useDebounce';

interface AgentEditModalProps {
  open: boolean;
  onClose: () => void;
  agentId?: number;
  onSuccess: () => void;
}

interface AssignmentRow {
    trackingNumberId: number | '';
    usageCategory: AgentTrackingNumberUsageCategory | '';
    isPrimary: boolean;
    priority: number;
}

export const AgentEditModal = ({ open, onClose, agentId, onSuccess }: AgentEditModalProps) => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  // Data Sources
  const [providers, setProviders] = useState<{ id: number; name: string }[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [queues, setQueues] = useState<CallQueue[]>([]);
  const [trackingNumbers, setTrackingNumbers] = useState<TrackingNumberListItem[]>([]);

  // Form State
  const [selectedProvider, setSelectedProvider] = useState<{ id: number; name: string } | null>(null);
  const [name, setName] = useState('');
  const [status, setStatus] = useState<AgentAvailabilityStatus>(AgentAvailabilityStatus.OFFLINE);
  const [roles, setRoles] = useState<string[]>(['CALLS_VIEWER', 'SMS_VIEWER']);
  const [teamAssignments, setTeamAssignments] = useState<TeamAssignment[]>([]);
  const [selectedQueues, setSelectedQueues] = useState<CallQueue[]>([]);
  
  // Tracking Number Assignments
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);

  // Search State
  const [providerSearch, setProviderSearch] = useState('');

  const debouncedProviderSearch = useDebounce((searchTerm: string) => {
      if (searchTerm && searchTerm.length >= 2) {
          fetchProviders(searchTerm);
      }
  });

  useEffect(() => {
    if (open) {
      fetchMetadata();
      if (agentId) {
        fetchAgentDetails(agentId);
      } else {
        resetForm();
      }
    }
  }, [open, agentId]);

  const resetForm = () => {
    setSelectedProvider(null);
    setName('');
    setStatus(AgentAvailabilityStatus.OFFLINE);
    setRoles(['CALLS_VIEWER', 'SMS_VIEWER']);
    setTeamAssignments([]);
    setSelectedQueues([]);
    setProviders([]);
    setProviderSearch('');
    setAssignments([]);
  };

  const fetchMetadata = async () => {
    try {
      const [teamsRes, routesRes, numbersRes] = await Promise.all([
        teamsService.getAllTeams(),
        queuesService.getAllRoutes(),
        voiceService.listTrackingNumbers(),
      ]);

      if (teamsRes.success && teamsRes.data) setTeams(teamsRes.data);

      if (routesRes.success && routesRes.data) {
        const allQueues = routesRes.data.flatMap((r) => r.callQueues || []);
        setQueues(allQueues);
      }

      if (numbersRes.success && numbersRes.data) setTrackingNumbers(numbersRes.data);
    } catch (error) {
      notify('Failed to load metadata', { type: 'error' });
    }
  };

  const fetchProviders = async (search: string) => {
      try {
          const res = await coreServiceReal.fetchMembers({ target: 'providers', searchTerm: search });
          if (res.success && res.data) {
              setProviders(res.data.map((p: any) => ({ id: p.id, name: p.fullName })));
          }
      } catch (error) {
          console.error('Error fetching providers', error);
      }
  }

  const fetchAgentDetails = async (id: number) => {
    setLoading(true);
    try {
      const res = await agentsService.getAgent(id);
      if (res.success && res.data) {
        const data = res.data;
        // For existing agents, we might not have the provider in the initial search list
        // So we might need to manually set it if we have the name
        setSelectedProvider({ id: Number(data.coreProviderId), name: data.name || 'Unknown' }); // Fallback name
        
        setName(data.name || '');
        setStatus(data.status);
        setRoles(data.roles);

        // Map agentTeams to teamAssignments with roles
        setTeamAssignments(
          data.agentTeams?.map((at) => ({
            teamId: at.team.id,
            teamRole: at.teamRole || 'MEMBER',
            team: at.team,
          })) || []
        );
        setSelectedQueues(data.queues || []);

        // Populate assignments
        if (data.agentTrackingNumbers) {
            // Note: The `AgentDetail` interface might need adjustment if `agentTrackingNumbers` structure from API 
            // is different or if `usageCategory` is nested differently.
            // Assuming `agentTrackingNumbers` contains the join table data including `usageCategory`.
            // Let's verify what `agentsService.getAgent` returns (it returns `AgentDetail`).
            // `AgentDetail` has `agentTrackingNumbers: { trackingNumber: TrackingNumberDetail }[]`.
            // This interface definition in `calls.ts` might be incomplete if we need usageCategory.
            // However, the backend returns the join table `agentTrackingNumbers` which has `usageCategory`.
            // So I will cast it to any to access properties that might be missing in strict types or assume they are there.
            
            const loadedAssignments = (data.agentTrackingNumbers as any[]).map((atn: any) => ({
                trackingNumberId: atn.trackingNumberId,
                usageCategory: atn.usageCategory,
                isPrimary: atn.isPrimary,
                priority: atn.priority || 1,
            }));
            setAssignments(loadedAssignments);
        }
      }
    } catch (error) {
      notify('Failed to load agent details', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAssignment = () => {
      setAssignments([...assignments, { trackingNumberId: '', usageCategory: '', isPrimary: false, priority: 1 }]);
  };

  const handleRemoveAssignment = (index: number) => {
      const newAssignments = [...assignments];
      newAssignments.splice(index, 1);
      setAssignments(newAssignments);
  };

  const handleAssignmentChange = (index: number, field: keyof AssignmentRow, value: any) => {
      const newAssignments = [...assignments];
      newAssignments[index] = { ...newAssignments[index], [field]: value };
      setAssignments(newAssignments);
  };

  // Team Assignment Helpers
  const handleAddTeam = (team: Team | null) => {
    if (team && !teamAssignments.some((ta) => ta.teamId === team.id)) {
      setTeamAssignments([
        ...teamAssignments,
        { teamId: team.id, teamRole: 'MEMBER', team },
      ]);
    }
  };

  const handleRemoveTeam = (teamId: number) => {
    setTeamAssignments(teamAssignments.filter((ta) => ta.teamId !== teamId));
  };

  const handleTeamRoleChange = (teamId: number, role: AgentTeamRole) => {
    setTeamAssignments(
      teamAssignments.map((ta) =>
        ta.teamId === teamId ? { ...ta, teamRole: role } : ta
      )
    );
  };

  // Teams available for selection (not already assigned)
  const availableTeams = useMemo(
    () => teams.filter((t) => !teamAssignments.some((ta) => ta.teamId === t.id)),
    [teams, teamAssignments]
  );

  const handleSave = async () => {
    setLoading(true);
    try {
      // Filter out incomplete assignments
      const validAssignments = assignments.filter(a => a.trackingNumberId && a.usageCategory).map(a => ({
          trackingNumberId: Number(a.trackingNumberId),
          usageCategory: a.usageCategory as AgentTrackingNumberUsageCategory,
          isPrimary: a.isPrimary,
          priority: Number(a.priority)
      }));

      const payload: any = {
        name,
        roles,
        teamAssignments: teamAssignments.map((ta) => ({
          teamId: ta.teamId,
          teamRole: ta.teamRole,
        })),
        queueIds: selectedQueues.map((q) => q.id!),
        trackingNumbers: validAssignments,
      };

      if (agentId) {
        // Update
        payload.status = status;

        await agentsService.updateAgent(agentId, payload);
        notify('Agent updated successfully', { type: 'success' });
      } else {
        // Create
        if (!selectedProvider) {
          notify('Provider is required', { type: 'warning' });
          setLoading(false);
          return;
        }
        payload.coreProviderId = selectedProvider.id;
        if (!payload.name) payload.name = selectedProvider.name;
        
        await agentsService.createAgent(payload);
        notify('Agent created successfully', { type: 'success' });
      }
      onSuccess();
      onClose();
    } catch (error) {
      notify('Failed to save agent', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleProviderInputChange = (event: any, newInputValue: string) => {
      setProviderSearch(newInputValue);
      debouncedProviderSearch(newInputValue);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>{agentId ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
      <DialogContent>
        <Box mt={2} display='flex' flexDirection='column' gap={3}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {agentId ? (
                <TextField
                  fullWidth
                  label='Core Provider'
                  value={selectedProvider?.name || selectedProvider?.id || ''}
                  disabled
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <Autocomplete
                  options={providers}
                  getOptionLabel={(option) => option.name}
                  value={selectedProvider}
                  onChange={(_, value) => {
                    setSelectedProvider(value);
                    if (value && !name) setName(value.name);
                  }}
                  onInputChange={handleProviderInputChange}
                  renderInput={(params) => (
                    <TextField {...params} label='Search Provider' helperText="Type at least 2 characters" />
                  )}
                  filterOptions={(x) => x} // Disable client-side filtering
                  loading={false} 
                />
              )}
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label='Display Name'
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label='Status'
                  onChange={(e) => setStatus(e.target.value as AgentAvailabilityStatus)}>
                  {Object.values(AgentAvailabilityStatus).map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
                <Autocomplete
                    multiple
                    options={['CALLS_ADMIN', 'CALLS_MANAGER', 'CALLS_VIEWER', 'SMS_ADMIN', 'SMS_MANAGER', 'SMS_VIEWER']}
                    value={roles}
                    onChange={(_, newValue) => setRoles(newValue)}
                    renderInput={(params) => <TextField {...params} label="Roles" />}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip size="small" label={option} {...getTagProps({ index })} />
                        ))
                    }
                />
            </Grid>
          </Grid>

          {/* Teams with Roles */}
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Teams
            </Typography>
            <Autocomplete
              options={availableTeams}
              getOptionLabel={(option) => option.name}
              value={null}
              onChange={(_, newValue) => handleAddTeam(newValue)}
              renderInput={(params) => (
                <TextField {...params} label='Add Team' size='small' />
              )}
              size='small'
              sx={{ mb: 1 }}
            />
            {teamAssignments.length > 0 ? (
              <Box display='flex' flexDirection='column' gap={1}>
                {teamAssignments.map((ta) => (
                  <Box
                    key={ta.teamId}
                    display='flex'
                    alignItems='center'
                    gap={1}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Chip
                      label={ta.team?.name || `Team ${ta.teamId}`}
                      onDelete={() => handleRemoveTeam(ta.teamId)}
                      size='small'
                      sx={{ minWidth: 100 }}
                    />
                    <FormControl size='small' sx={{ minWidth: 100 }}>
                      <Select
                        value={ta.teamRole}
                        onChange={(e) =>
                          handleTeamRoleChange(ta.teamId, e.target.value as AgentTeamRole)
                        }
                        size='small'
                      >
                        <MenuItem value='MEMBER'>Member</MenuItem>
                        <MenuItem value='LEADER'>Leader</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant='body2' color='textSecondary'>
                No teams assigned.
              </Typography>
            )}
          </Box>

          {/* Queues */}
          <Autocomplete
            multiple
            options={queues}
            getOptionLabel={(option) =>
              `${option.ivrDigit ? `[${option.ivrDigit}] ` : ''}${option.displayName}`
            }
            value={selectedQueues}
            onChange={(_, newValue) => setSelectedQueues(newValue)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label='Queues' />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size='small'
                  label={option.displayName}
                  {...getTagProps({ index })}
                />
              ))
            }
          />

          <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1">Tracking Numbers</Typography>
                  <Button startIcon={<AddIcon />} size="small" onClick={handleAddAssignment}>Add Number</Button>
              </Box>
              {assignments.map((assignment, index) => (
                  <Grid container spacing={2} key={index} alignItems="center" sx={{ mb: 1 }}>
                      <Grid item xs={4}>
                          <FormControl fullWidth size="small">
                              <InputLabel>Number</InputLabel>
                              <Select
                                  value={assignment.trackingNumberId}
                                  label="Number"
                                  onChange={(e) => handleAssignmentChange(index, 'trackingNumberId', e.target.value)}
                              >
                                  {trackingNumbers.map((tn) => (
                                      <MenuItem key={tn.id} value={tn.id}>
                                          {tn.trackingNumber} {tn.label ? `(${tn.label})` : ''}
                                      </MenuItem>
                                  ))}
                              </Select>
                          </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                          <FormControl fullWidth size="small">
                              <InputLabel>Usage</InputLabel>
                              <Select
                                  value={assignment.usageCategory}
                                  label="Usage"
                                  onChange={(e) => handleAssignmentChange(index, 'usageCategory', e.target.value)}
                              >
                                  {Object.values(AgentTrackingNumberUsageCategory).map((cat) => (
                                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                  ))}
                              </Select>
                          </FormControl>
                      </Grid>
                      <Grid item xs={2}>
                          <FormControlLabel
                              control={<Switch checked={assignment.isPrimary} onChange={(e) => handleAssignmentChange(index, 'isPrimary', e.target.checked)} size="small" />}
                              label="Primary"
                              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                          />
                      </Grid>
                      <Grid item xs={1}>
                          <IconButton size="small" color="error" onClick={() => handleRemoveAssignment(index)}>
                              <DeleteIcon fontSize="small" />
                          </IconButton>
                      </Grid>
                  </Grid>
              ))}
              {assignments.length === 0 && (
                  <Typography variant="body2" color="textSecondary">No tracking numbers assigned.</Typography>
              )}
          </Box>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant='contained' disabled={loading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
